import { useImmerAtom } from 'jotai-immer';
import utilSvc from '../../../../core/utilSvc';
import validSvc from '../../../../core/validSvc';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { ModelProp, EditModelHook, StatusVal } from '../payor_edit_types';
import { HttpUpsert200, HttpError422 } from '../../../../../global';
import { editModelAtom } from '../payor_edit_atoms';
import payorRepo from '../../data/payorRepo';
import { usePayorEditStore } from '../pagehooks/usePayorEditStore';
import { usePayorEditState } from './usePayorEditState';
import { ValidPropText } from '../../../../core/components/validation/ValidInputText';
import {
    scrubValue,
    validateBIN,
    validateMedType,
    validatePCNOrGroupNumber,
} from '../../../../core/components/validation/validators';
import { useCallback } from 'react';

const validatePayorName = (prop: ValidPropText): boolean => {
    prop.err = '';
    const val = scrubValue(prop);
    if (val && (val.length < 2 || val.length > 100)) {
        prop.err = 'Optional, 2 to 100 characters.';
        return false;
    }
    return true;
};

const usePayorEditModel = (): EditModelHook => {
    const { state: pageState, cancelEdit } = usePayorEditStore();
    const { waiting, setWait } = usePayorEditState();
    const [model, setModel] = useImmerAtom(editModelAtom);

    const setPropText = useCallback(
        (prop: ModelProp, val: string): void => {
            setModel(draft => {
                draft[prop].val = val;
            });
        },
        [setModel],
    );

    // model validate all props
    const validate = useCallback((): boolean => {
        let result = true;
        setModel(draft => {
            result = result && validateMedType(draft.MedType);
            result = result && validateBIN(draft.BIN);
            result = result && validatePCNOrGroupNumber(draft.PCN);
            result = result && validatePCNOrGroupNumber(draft.PCN);
            result = result && validatePayorName(draft.Name);
        });
        return result;
    }, [setModel]);

    const submitAsync = useCallback(async (): Promise<void> => {
        // API: CREATE / UPDATE

        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const r = (await payorRepo.upsert(pageState.mode, model)) as HttpUpsert200;
            ngDelegate.ok('Payor Saved');

            // switch to UPDATE
            if (pageState.mode == 'CREATE') {
                cancelEdit();
            }
        } catch (err: any) {
            console.log('STORE POST ERR', err);
            if (utilSvc.http.isIgnore(err)) {
                return;
            }

            // handle 422 as model
            setModel(() => {
                const errMsg = validSvc.http422ToModel(err as HttpError422, model);
                ngDelegate.ok('Cannot Save', errMsg);
            });
        } finally {
            setWait(false);
        }
    }, [cancelEdit, model, pageState.mode, setModel, setWait]);

    const statusChangeAsync = useCallback(
        async (status: StatusVal): Promise<void> => {
            // API: STATUS CHANGE

            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const r = (await payorRepo.updateStatus(model.Id, status as string)) as HttpUpsert200;
                ngDelegate.ok('Payor Status Changed to: ' + status);

                // switch status
                setModel(draft => {
                    draft.Status.val = status;
                });
                ngDelegate.ok('Status Changed');
            } catch (err: any) {
                console.log('STORE POST ERR');
                if (utilSvc.http.isIgnore(err)) {
                    return;
                }

                // handle 409?
                ngDelegate.ok('Cannot Save', err);
            } finally {
                setWait(false);
            }
        },
        [model.Id, setModel, setWait],
    );

    const submit = useCallback(() => {
        if (waiting) {
            return false;
        }

        if (!validate()) {
            ngDelegate.ok('Cannot Save', 'Check properties');
            return false;
        }

        setWait(true);
        submitAsync();
        return true;
    }, [setWait, submitAsync, validate, waiting]);

    const setStatus = useCallback(
        (status: StatusVal) => {
            if (waiting) {
                return;
            }
            statusChangeAsync(status);
        },
        [statusChangeAsync, waiting],
    );

    return {
        model,
        setPropText,
        setStatus,
        submit,
    };
};

export default usePayorEditModel;
